import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import deepEqual from 'fast-deep-equal';
import Editor from "@monaco-editor/react";
import { 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Switch, 
  FormControlLabel,
  Box, 
  Chip, 
  Tooltip, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  Divider
} from '@mui/material';
import { Plus, Minus, Wand2, Maximize2, Minimize2 } from 'lucide-react';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../../config/models';
import InferenceChainStep from '../../../models/Steps/InferenceChainStep';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'primary';
    case 'Low': return 'success';
    case 'Very Low': return 'info';
    default: return 'default';
  }
};

interface InferenceStepBoxProps {
  stepData: any;
  onUpdate?: (step: any) => void;
  onUpdateImmediate?: (step: any) => void;
  workflowInputs?: {};
  setWorkflowInputs: any
}

const InferenceStepBox: React.FC<InferenceStepBoxProps> = ({ 
  stepData, 
  onUpdate,
  onUpdateImmediate,
  workflowInputs = {},
  setWorkflowInputs
}) => {
  // UI-only state
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPromptMaximized, setIsPromptMaximized] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const monacoRef = useRef<any>(null);
  
  // Local state for input values to prevent focus loss
  const [localDescription, setLocalDescription] = useState(stepData.description);
  const [localModel, setLocalModel] = useState(stepData.definition.model || DEFAULT_MODEL);
  const [localModelTemp, setLocalModelTemp] = useState(stepData.definition.model_temp || DEFAULT_TEMPERATURE);
  const [localPrompt, setLocalPrompt] = useState(stepData.definition.prompt || '');
  const [localVariables, setLocalVariables] = useState(stepData.definition.variables || {});
  const [localIsStreaming, setLocalIsStreaming] = useState(stepData.definition.is_streaming || false);
  const [localCondition, setLocalCondition] = useState(stepData.definition.condition || 'True');

  // Update local state when stepData changes
  useEffect(() => {
    setLocalDescription(stepData.description);
    setLocalModel(stepData.definition.model || DEFAULT_MODEL);
    setLocalModelTemp(stepData.definition.model_temp || DEFAULT_TEMPERATURE);
    setLocalPrompt(stepData.definition.prompt || '');
    setLocalVariables(stepData.definition.variables || {});
    setLocalIsStreaming(stepData.definition.is_streaming || false);
    setLocalCondition(stepData.definition.condition || 'True');
  }, [stepData.id]); // Only update when step ID changes

  // Handlers for local state changes
  const updateParent = useCallback((field: string, value: any) => {
    if (!onUpdate) return;
    const definition = {
      ...stepData.definition,
      [field]: value
    };
    const updatedStep = new InferenceChainStep(
      field === 'description' ? value : localDescription,
      definition
    );
    updatedStep.id = stepData.id;
    onUpdate(updatedStep);
  }, [onUpdate, stepData.definition, stepData.id, localDescription]);

  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value);
  };

  const handleDescriptionBlur = () => {
    if (onUpdateImmediate) {
      const definition = {
        ...stepData.definition,
        description: localDescription
      };
      const updatedStep = new InferenceChainStep(
        localDescription,
        definition
      );
      updatedStep.id = stepData.id;
      onUpdateImmediate(updatedStep);
    } else {
      updateParent('description', localDescription);
    }
  };

  const handleModelChange = (value: string) => {
    setLocalModel(value);
    updateParent('model', value);
  };

  const handleModelTempChange = (value: number) => {
    setLocalModelTemp(value);
    updateParent('model_temp', value);
  };

  const handlePromptChange = (newPrompt: string | undefined) => {
    const prompt = newPrompt || '';
    setLocalPrompt(prompt);
    updateParent('prompt', prompt);
  };

  const handleVariablesChange = (variables: any) => {
    setLocalVariables(variables);
    updateParent('variables', variables);
  };

  const handleIsStreamingChange = (value: boolean) => {
    setLocalIsStreaming(value);
    updateParent('is_streaming', value);
  };

  const handleConditionChange = (value: string) => {
    setLocalCondition(value);
    updateParent('condition', value);
  };

  const handleEnhancePrompt = async () => {
    if (!stepData.definition.prompt?.trim()) return;
    setIsEnhancing(true);
    try {
      const response = await fetch('/api/enhance-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: stepData.definition.prompt, model: stepData.definition.model, temperature: stepData.definition.model_temp })
      });
      if (!response.ok) throw new Error('Failed to enhance prompt');
      const data = await response.json();
      if (data.enhancedPrompt) {
        handlePromptChange(data.enhancedPrompt);
      }
    } catch (error) {
      // handle error
    } finally {
      setIsEnhancing(false);
    }
  };

  // UI rendering
  return (
    <Paper sx={{ p: 2, bgcolor: 'grey.100', border: 1, borderColor: 'grey.300', fontSize: '0.875rem' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() => setIsCollapsed(v => !v)}
            size="small"
            sx={{ mr: 1 }}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Inference Chain Step</Typography>
        </Box>
      </Box>
      {!isCollapsed && (
        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'grey.700' }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>ID:</Typography>
            <TextField
              value={stepData.id}
              InputProps={{ readOnly: true }}
              sx={{ bgcolor: 'grey.200', '& .MuiInputBase-input': { cursor: 'not-allowed' } }}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Description:</Typography>
            <TextField
              value={localDescription}
              onChange={e => handleDescriptionChange(e.target.value)}
              onBlur={handleDescriptionBlur}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Model:</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={localModel}
                onChange={e => handleModelChange(e.target.value)}
                label="Model"
              >
                {AVAILABLE_MODELS.map(model => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{model.name}</Typography>
                      <Typography variant="caption" color="textSecondary">{model.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Model Temperature:</Typography>
            <TextField
              type="number"
              value={localModelTemp}
              onChange={e => handleModelTempChange(parseFloat(e.target.value))}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Prompt:</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {Object.entries(workflowInputs).map(([key]) => (
                  <Tooltip title="Referenced Variables" key={key}>
                    <Box display="flex" gap={0.5}>
                      <Chip label={key} color="error" size="small" />
                    </Box>
                  </Tooltip>
                ))}
                <Button size="small" variant="outlined" onClick={() => setVariableModalOpen(true)}>
                  Insert Variable
                </Button>
                <IconButton size="small" onClick={() => setIsPromptMaximized(true)}>
                  {isPromptMaximized ? <Minimize2 width={16} height={16} /> : <Maximize2 width={16} height={16} />}
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                onClick={handleEnhancePrompt}
                disabled={isEnhancing || !localPrompt?.trim()}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {isEnhancing ? (
                  <CircularProgress size={16} />
                ) : (
                  <Wand2 width={16} height={16} />
                )}
                <Typography sx={{ ml: 0.5 }}>Enhance Prompt</Typography>
              </Button>
            </Box>
            <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              <Editor
                height="200px"
                defaultLanguage="markdown"
                value={localPrompt}
                onChange={handlePromptChange}
                theme="vs-light"
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  automaticLayout: true
                }}
                onMount={(editor) => { monacoRef.current = editor; }}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localIsStreaming}
                  onChange={e => handleIsStreamingChange(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>Enable Streaming:</Typography>}
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            {!localCondition ? (
              <Button 
                variant="outlined" 
                startIcon={<Plus width={16} height={16} />}
                onClick={() => handleConditionChange('')}
                fullWidth
              >
                Add Condition
              </Button>
            ) : (
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Step Condition (Python Code):</Typography>
                  <Button 
                    variant="text" 
                    color="error"
                    startIcon={<Minus width={16} height={16} />}
                    onClick={() => handleConditionChange('')}
                  >
                    Remove
                  </Button>
                </Box>
                <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                  <Editor
                    height="60px"
                    defaultLanguage="python"
                    value={localCondition}
                    onChange={val => handleConditionChange(val || '')}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 14,
                      lineNumbers: 'off',
                      glyphMargin: false,
                      folding: false,
                      lineDecorationsWidth: 0,
                      lineNumbersMinChars: 0,
                      automaticLayout: true
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Dialog
        open={isPromptMaximized}
        onClose={() => setIsPromptMaximized(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh', mt: 2.5 } }}
      >
        <DialogTitle>Prompt Editor</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 'calc(100% - 60px)' }}>
            <Editor
              height="100%"
              defaultLanguage="markdown"
                              value={localPrompt}
              onChange={handlePromptChange}
              theme="vs-light"
              options={{
                minimap: { enabled: true },
                scrollBeyondLastLine: true,
                fontSize: 14,
                lineNumbers: 'on',
                wordWrap: 'on',
                automaticLayout: true
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPromptMaximized(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEnhancePrompt}
            disabled={isEnhancing || !localPrompt?.trim()}
            startIcon={isEnhancing ? <CircularProgress size={16} /> : <Wand2 width={16} height={16} />}
          >
            Enhance Prompt
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={variableModalOpen} onClose={() => setVariableModalOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          Insert Workflow Variables
        </DialogTitle>
        <DialogContent sx={{ minWidth: 350, p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select one or more workflow input variables to insert at the cursor position in your prompt.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Autocomplete
            multiple
            options={Object.keys(workflowInputs)}
            value={selectedVariables}
            onChange={(_, newValue) => setSelectedVariables(newValue)}
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip label={option} {...getTagProps({ index })} color="primary" />
                ))
              }
            renderInput={(params) => (
              <TextField {...params} label="Workflow Inputs" placeholder="Select variables" />
            )}
            disableCloseOnSelect
            noOptionsText="No workflow inputs available"
          />
          </Paper>
          <Divider sx={{ my: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVariableModalOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              let newPrompt = localPrompt;
              const insertText = selectedVariables.map(v => `{${v}}`).join(' ');
              if (monacoRef.current) {
                const editor = monacoRef.current;
                const selection = editor.getSelection();
                const range = selection ? editor.getModel().getValueInRange(selection) : '';
                const pos = selection ? editor.getModel().getOffsetAt(selection.getStartPosition()) : newPrompt.length;
                newPrompt = newPrompt.slice(0, pos) + insertText + newPrompt.slice(pos);
              } else {
                newPrompt += (newPrompt && !newPrompt.endsWith(' ')? ' ' : '') + insertText;
              }
              setLocalPrompt(newPrompt);
              updateParent('prompt', newPrompt);
              setVariableModalOpen(false);
              setSelectedVariables([]);
            }}
            disabled={selectedVariables.length === 0}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default React.memo(InferenceStepBox);